import { ExportButton } from "@components/exports/ExportButton";
import { CopyButton } from "@components/shared/CopyButton";
import {
  CASCADE_GENERATE_BLUEPRINT_ARTEFACTS,
  EXPORT_INTERVIEW_GUIDE_TO_GOOGLE_DOCS,
  UPDATE_INTERVIEW_GUIDE,
} from "@lib/graphql/mutations";
import { SanitizedHtml } from "@lib/SanitizedHtml";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Heading1,
  Heading2,
  Italic,
  List,
  ListOrdered,
  Loader2,
  Pencil,
  Redo,
  Sparkles,
  Undo,
} from "lucide-react";
import { useRef, useState } from "react";
import { useMutation } from "urql";

interface InterviewGuideAuthor {
  id: string;
  name: string;
}

interface InterviewGuideVersionData {
  id: string;
  versionNumber: number;
  changeSummary: string | null;
  effectiveFrom: string;
  author: InterviewGuideAuthor;
}

interface InterviewGuideCurrentVersion extends InterviewGuideVersionData {
  content: string | null;
  generationState?: string;
  generationError?: string | null;
  model?: string | null;
  roleBlueprintVersionNumber?: number | null;
}

export interface InterviewGuideData {
  id: string;
  selectionStepId: string | null;
  stepType: string | null;
  totalVersions: number;
  currentVersion: InterviewGuideCurrentVersion | null;
  versions: InterviewGuideVersionData[];
}

// Story #2252: guide identity is now per-SelectionStep. The panel takes the
// step's id + step_type instead of the legacy screening/client `kind` enum.
interface InterviewGuidePanelProps {
  campaignId: string;
  selectionStepId: string;
  stepType: string;
  guide: InterviewGuideData | null;
  blueprintLocked?: boolean;
  onSaved?: () => void;
  // When true, render the panel collapsed: only the header (title, version
  // pill, action buttons) shows; the body and version-history block are
  // hidden behind a Show/Hide toggle. Used by the Interviews sub-tabs so
  // candidates remain visible without scrolling past a full guide.
  defaultCollapsed?: boolean;
}

const STEP_TYPE_TITLES: Record<string, string> = {
  recruiter_call: "Recruiter Call Guide",
  recruiter_interview: "Recruiter Interview Guide",
  interview: "Interview Guide",
  final: "Final Interview Guide",
};

function titleForStepType(stepType: string): string {
  return STEP_TYPE_TITLES[stepType] ?? "Interview Guide";
}

function emptyStateForStepType(stepType: string): string {
  return `Draft the questions, probes and logistics for the ${stepType.replace(/_/g, " ")} step. Matrix-level evidence indicators belong on the Assessment Guide, not here.`;
}

export function InterviewGuidePanel({
  campaignId,
  selectionStepId,
  stepType,
  guide,
  blueprintLocked = false,
  onSaved,
  defaultCollapsed = false,
}: InterviewGuidePanelProps) {
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [{ fetching: saving }, updateGuide] = useMutation(UPDATE_INTERVIEW_GUIDE);
  const [{ fetching: regenerating }, cascadeGenerate] = useMutation(
    CASCADE_GENERATE_BLUEPRINT_ARTEFACTS,
  );
  const [, exportToGoogleDocs] = useMutation(EXPORT_INTERVIEW_GUIDE_TO_GOOGLE_DOCS);
  const bodyRef = useRef<HTMLDivElement>(null);

  const currentVersion = guide?.currentVersion ?? null;
  const hasGuide = guide !== null && currentVersion !== null;
  const copy = {
    title: titleForStepType(stepType),
    emptyState: emptyStateForStepType(stepType),
  };

  const generating = currentVersion?.generationState === "generating";
  const regenerateDisabled = !blueprintLocked || generating || regenerating;
  const regenerateTitle = !blueprintLocked
    ? "Lock the blueprint first to regenerate from it"
    : generating
      ? "Generation already in progress"
      : "Regenerate this guide from the locked blueprint";

  const handleRegenerate = async () => {
    if (regenerateDisabled) return;
    // Story #2252: the cascade fans out across every interview-flavoured step;
    // a single `interview_guide` kind covers them all.
    await cascadeGenerate({ input: { campaignId, kinds: ["interview_guide"] } });
    onSaved?.();
  };

  const handleExportStart = async () => {
    const result = await exportToGoogleDocs({ input: { selectionStepId } });
    const payload = result.data?.exportInterviewGuideToGoogleDocs;
    return {
      documentExportId: payload?.documentExport?.id ?? null,
      errors: payload?.errors ?? [],
    };
  };

  return (
    <div
      className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
      data-testid="interview-guide-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed || editing}
          aria-label={collapsed ? `Show ${copy.title}` : `Hide ${copy.title}`}
          className="flex items-center gap-2 rounded text-left hover:bg-card-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {collapsed && !editing ? (
            <ChevronRight size={16} className="text-text-faint" />
          ) : (
            <ChevronDown size={16} className="text-text-faint" />
          )}
          <ClipboardList size={18} className="text-text-faint" />
          <div>
            <h2 className="text-sm font-medium text-text-primary">{copy.title}</h2>
            <p className="text-xs text-text-faint">
              {hasGuide
                ? `Version ${currentVersion.versionNumber} · ${guide.totalVersions} total`
                : `No ${copy.title.toLowerCase()} yet`}
            </p>
          </div>
        </button>
        <div className="flex items-center gap-2">
          {hasGuide && (
            <ExportButton
              onStart={handleExportStart}
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
            />
          )}
          {hasGuide && (
            <CopyButton
              ariaLabel="Copy interview guide"
              withLabel
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover"
              getContent={() => {
                const el = bodyRef.current;
                if (!el) return { html: "", text: "" };
                return {
                  html: el.innerHTML,
                  text: el.innerText || el.textContent || "",
                };
              }}
            />
          )}
          {!editing && (
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={regenerateDisabled}
              title={regenerateTitle}
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Regenerate from blueprint
            </button>
          )}
          {!editing && (
            <button
              type="button"
              onClick={() => {
                // Auto-expand when entering the editor so the user sees what
                // they're editing — otherwise the editor would render below
                // a collapsed header which is disorienting.
                setCollapsed(false);
                setEditing(true);
              }}
              className="flex items-center gap-1 rounded bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent/90"
            >
              <Pencil size={14} />
              {hasGuide ? "Edit" : "Create"}
            </button>
          )}
        </div>
      </div>

      {!editing && hasGuide && !collapsed && (
        <div className="mt-3">
          <SanitizedHtml
            ref={bodyRef}
            className="prose prose-sm dark:prose-invert max-w-none text-text-primary"
            html={currentVersion.content}
          />
        </div>
      )}

      {!editing && !hasGuide && !collapsed && (
        <p className="mt-3 text-sm text-text-muted">{copy.emptyState}</p>
      )}

      {editing && (
        <InterviewGuideEditor
          campaignId={campaignId}
          initialContent={currentVersion?.content ?? ""}
          saving={saving}
          onCancel={() => setEditing(false)}
          onSave={async (content, changeSummary) => {
            const result = await updateGuide({
              input: { selectionStepId, content, changeSummary: changeSummary || null },
            });
            if (result.data?.updateInterviewGuide?.errors?.length === 0) {
              setEditing(false);
              onSaved?.();
            }
          }}
        />
      )}

      {hasGuide && guide.versions.length > 1 && !collapsed && (
        <div className="mt-3 border-t border-border pt-3">
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1 text-xs font-medium text-text-secondary hover:text-text-primary"
          >
            {showHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Version history ({guide.versions.length - 1} prior)
          </button>
          {showHistory && (
            <ul className="mt-2 space-y-1 text-xs text-text-muted">
              {guide.versions
                .filter((v) => v.id !== currentVersion?.id)
                .map((v) => (
                  <li key={v.id} className="flex items-baseline justify-between gap-2">
                    <span>
                      v{v.versionNumber}
                      {v.changeSummary ? ` — ${v.changeSummary}` : ""}
                    </span>
                    <span className="text-text-faint">
                      {v.author.name} · {new Date(v.effectiveFrom).toLocaleString()}
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

interface InterviewGuideEditorProps {
  campaignId: string;
  initialContent: string;
  saving: boolean;
  onSave: (content: string, changeSummary: string | undefined) => void;
  onCancel: () => void;
}

function InterviewGuideEditor({
  initialContent,
  saving,
  onSave,
  onCancel,
}: InterviewGuideEditorProps) {
  const [changeSummary, setChangeSummary] = useState("");

  const editor = useEditor({
    extensions: [StarterKit],
    content: initialContent,
    editorProps: {
      attributes: {
        class:
          "prose prose-sm dark:prose-invert max-w-none min-h-[160px] p-4 focus:outline-none text-text-primary",
      },
    },
  });

  if (!editor) return null;

  return (
    <div className="mt-3 rounded-lg ring-1 ring-ring">
      <div className="flex flex-wrap items-center gap-1 border-b border-border p-2">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          title="Bold"
        >
          <Bold size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          title="Italic"
        >
          <Italic size={16} />
        </ToolbarButton>
        <div className="mx-1 h-5 w-px bg-border" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive("heading", { level: 1 })}
          title="Heading 1"
        >
          <Heading1 size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}
          title="Heading 2"
        >
          <Heading2 size={16} />
        </ToolbarButton>
        <div className="mx-1 h-5 w-px bg-border" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          title="Bullet list"
        >
          <List size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          title="Ordered list"
        >
          <ListOrdered size={16} />
        </ToolbarButton>
        <div className="mx-1 h-5 w-px bg-border" />
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
        >
          <Undo size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
        >
          <Redo size={16} />
        </ToolbarButton>
      </div>

      <EditorContent editor={editor} />

      <div className="flex items-center gap-3 border-t border-border p-3">
        <input
          type="text"
          value={changeSummary}
          onChange={(e) => setChangeSummary(e.target.value)}
          placeholder="What changed? (optional)"
          className="flex-1 rounded border border-input-border bg-card px-3 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-3 py-1.5 text-sm text-text-secondary hover:bg-card-hover"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSave(editor.getHTML(), changeSummary || undefined)}
          disabled={saving}
          className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function ToolbarButton({
  children,
  onClick,
  active,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`rounded p-1.5 ${
        active ? "bg-accent/10 text-accent" : "text-text-secondary hover:bg-card-hover"
      } disabled:opacity-30`}
    >
      {children}
    </button>
  );
}
